import React, {ReactElement} from "react";

import LandingPageHero from "./landing-page-hero/LandingPageHero";
import PageHero from "./page-hero/PageHero";
import BlogHero from "./blog-hero/BlogHero";
import SearchHero from "./search-hero/SearchHero";
import SlimHero from "./slim-hero/SlimHero";
import TextHero from "./text-hero/TextHero";
import LargeHero from "./large-hero/LargeHero";
import HomePageHeroWrapper from "./home-page-hero/HomePageHeroWrapper";

import {AlgoliaContext} from "/src/contexts/context";

import translate from "/src/utils/translate";
import {visibleForLocale} from "/src/utils/utils";
import {LOCALE} from "/src/config";
import {
  parseContentfulWistiaVideoJSON,
  constructWistiaVideoEmbedUrlById,
} from "/src/utils/wistia";
import {CTA} from "../buttons/cta-button/CTAButton";
import {ImageObject} from "../image/Image";
import {SimpleFormProps, MultiFormProps} from "/src/types";
import {VideoType} from "../videos/video/Video";
import SplitHero from "./split-hero/SplitHero";
import {FormData} from "../lead-forms/lead-form/LeadForm";
import {formattedPublishDate} from "/src/utils/blog";
import {QuoteProps} from "../cards/quote/Quote";
import StackedHero from "./stacked-hero/StackedHero";

export type HeroSideBySideModuleLayout =
  | "hardcoded_animation_hero"
  | "blog_hero"
  | "landing_page_hero"
  | "large_hero"
  | "slim_hero"
  | "search_hero"
  | "split_hero"
  | "stacked_hero"
  | "text_hero"
  | "page_hero";

export type HeroSideBySideModuleTheme = "light" | "dark" | "grey";

export interface HeroSideBySideModuleProps
  extends Omit<
    Queries.HeroSideBySideModuleFragmentFragment,
    "layout" | "theme" | "videoBehavior"
  > {
  layout: HeroSideBySideModuleLayout;
  theme: HeroSideBySideModuleTheme;
  videoBehavior: VideoType;
  onLeadFormSubmitSuccess: (data: FormData) => unknown;
  onLeadFormSubmitError: (err: Error) => unknown;
  isHomePage?: boolean;
}

const HeroSideBySideModule = (
  props: HeroSideBySideModuleProps
): ReactElement | null => {
  if (!props) {
    return null;
  }

  const {
    layout,
    fullbleed,
    eyebrow,
    eyebrowIcon,
    title,
    subtitle,
    body,
    ctaButtons,
    alignToRight, // replaced by "alignLayout"
    alignLayout,
    backgroundImage,
    backgroundVideo,
    wistiaBackgroundVideo,
    showHeroTransparentGrayOverlay,
    showLeadForm,
    onLeadFormSubmitSuccess,
    onLeadFormSubmitError,
    isHomePage,
    pardotFormHandlerUrl,
    leadFormHeadline,
    leadFormButtonText,
    leadFormCampaignId,
    leadFormConfirmationRoute,
    leadFormLastInquiryObject,
    titleWithLineBreaks,
    useTitleWithLineBreaks,
    theme,
    form,
    videoBehavior,
    videoControls,
    blogPost,
    visibleIn,
    showFormInDrawer,
    signUpServicesAbTestingAlternative,
    quote,
  } = props;

  if (!visibleForLocale(visibleIn as Array<string>, LOCALE)) {
    return null;
  }

  const darkMode = theme !== "light";

  const formTitle =
    leadFormHeadline != null
      ? leadFormHeadline
      : translate("Sign Up With Flexport");

  const formButtonText =
    leadFormButtonText != null ? leadFormButtonText : translate("Sign Up");

  const bgVideo = backgroundVideo?.file?.url;
  let wistiaVideo = null;

  if (wistiaBackgroundVideo != null) {
    const {internal} = wistiaBackgroundVideo;

    const {content} = internal;

    const wistiaContent = parseContentfulWistiaVideoJSON(content, true);

    if (wistiaContent) {
      // eslint-disable-next-line camelcase
      wistiaVideo = constructWistiaVideoEmbedUrlById(wistiaContent.hashed_id);
    }
  }

  const vBehavior: string = videoBehavior || "autoplay";
  const ctas = ctaButtons as unknown as Array<CTA>;
  const darkOverlay =
    showHeroTransparentGrayOverlay === true ||
    showHeroTransparentGrayOverlay == null;
  const formObj = form as SimpleFormProps | MultiFormProps;
  const quoteObj = quote as QuoteProps;

  switch (layout) {
    case "hardcoded_animation_hero": {
      return (
        <HomePageHeroWrapper
          titleWithLineBreaks={titleWithLineBreaks?.titleWithLineBreaks!}
          ctas={ctas}
          copy={body?.body!}
        />
      );
    }
    case "blog_hero": {
      return (
        <BlogHero
          author={blogPost?.author!}
          bgImage={backgroundImage as ImageObject}
          bgVideo={bgVideo!}
          wistiaBgVideo={wistiaVideo!}
          categories={blogPost?.categories}
          slug={blogPost?.slug!}
          title={title!}
          copy={body?.body!}
          eyebrow={formattedPublishDate(blogPost?.publishedAt!)!}
          type={blogPost?.type!}
          showBackBtn
          showSocialIcons
          videoBehavior={vBehavior as VideoType}
          videoControls={videoControls === true}
        />
      );
    }
    case "landing_page_hero": {
      return (
        <LandingPageHero
          eyebrow={eyebrow}
          eyebrowIcon={eyebrowIcon as ImageObject}
          headline={title}
          subtitle={subtitle}
          copy={body?.body}
          ctas={ctas}
          bgImage={backgroundImage as ImageObject}
          bgVideo={bgVideo}
          wistiaBgVideo={wistiaVideo!}
          pardotFormHandlerUrl={pardotFormHandlerUrl}
          leadFormHeadline={formTitle}
          leadFormButtonText={formButtonText}
          leadFormCampaignId={leadFormCampaignId}
          leadFormConfirmationRoute={leadFormConfirmationRoute}
          leadFormLastInquiryObject={leadFormLastInquiryObject}
          darkOverlay={darkOverlay}
          onLeadFormSubmitError={onLeadFormSubmitError}
          onLeadFormSubmitSuccess={onLeadFormSubmitSuccess}
          titleWithLineBreaks={titleWithLineBreaks?.titleWithLineBreaks}
          useTitleWithLineBreaks={useTitleWithLineBreaks}
          form={formObj}
        />
      );
    }
    case "slim_hero": {
      return (
        <SlimHero
          bgImage={backgroundImage as ImageObject}
          bgVideo={bgVideo}
          wistiaBgVideo={wistiaVideo!}
          copy={body?.body}
          ctas={ctas}
          eyebrow={eyebrow}
          eyebrowIcon={eyebrowIcon as ImageObject}
          headline={title}
          darkMode={darkMode}
          useTitleWithLineBreaks={useTitleWithLineBreaks}
          titleWithLineBreaks={titleWithLineBreaks?.titleWithLineBreaks}
          videoBehavior={vBehavior as VideoType}
          videoControls={videoControls === true}
        />
      );
    }
    case "search_hero": {
      return (
        <AlgoliaContext.Consumer>
          {(algoliaIndex: string) => (
            <SearchHero
              algoliaIndex={algoliaIndex}
              bgImage={backgroundImage as ImageObject}
              bgVideo={bgVideo}
              wistiaBgVideo={wistiaVideo!}
              copy={body?.body}
              ctas={ctas}
              eyebrow={eyebrow}
              eyebrowIcon={eyebrowIcon as ImageObject}
              headline={title}
              darkMode={darkMode}
              useTitleWithLineBreaks={useTitleWithLineBreaks}
              titleWithLineBreaks={titleWithLineBreaks?.titleWithLineBreaks}
              videoBehavior={vBehavior as VideoType}
              videoControls={videoControls === true}
            />
          )}
        </AlgoliaContext.Consumer>
      );
    }
    case "split_hero": {
      return (
        <SplitHero
          eyebrow={eyebrow!}
          eyebrowIcon={eyebrowIcon as ImageObject}
          headline={title!}
          copy={body?.body!}
          ctas={ctas}
          bgImage={backgroundImage as ImageObject}
          bgVideo={bgVideo!}
          wistiaBgVideo={wistiaVideo!}
          theme={theme}
          useTitleWithLineBreaks={useTitleWithLineBreaks!}
          titleWithLineBreaks={titleWithLineBreaks?.titleWithLineBreaks!}
          videoBehavior={vBehavior as VideoType}
          videoControls={videoControls === true}
          form={form as SimpleFormProps}
          largeHeadline={isHomePage}
          showFormInDrawer={showFormInDrawer === true}
          signUpServicesAbTestingAlternative={
            signUpServicesAbTestingAlternative === true
          }
        />
      );
    }
    case "stacked_hero": {
      return (
        <StackedHero
          eyebrow={eyebrow!}
          eyebrowIcon={eyebrowIcon as ImageObject}
          headline={title!}
          copy={body?.body!}
          ctas={ctas}
          bgImage={backgroundImage as ImageObject}
          bgVideo={bgVideo!}
          wistiaBgVideo={wistiaVideo!}
          theme={theme}
          useTitleWithLineBreaks={useTitleWithLineBreaks!}
          titleWithLineBreaks={titleWithLineBreaks?.titleWithLineBreaks!}
          videoBehavior={vBehavior as VideoType}
          videoControls={videoControls === true}
          form={form as SimpleFormProps}
          largeHeadline={isHomePage}
          showFormInDrawer={showFormInDrawer === true}
          signUpServicesAbTestingAlternative={
            signUpServicesAbTestingAlternative === true
          }
        />
      );
    }
    case "text_hero": {
      return (
        <TextHero
          fullbleed={fullbleed}
          eyebrow={eyebrow}
          headline={title}
          copy={body?.body}
          ctas={ctas}
          darkMode={darkMode}
          useTitleWithLineBreaks={useTitleWithLineBreaks}
          titleWithLineBreaks={titleWithLineBreaks?.titleWithLineBreaks}
        />
      );
    }
    case "large_hero": {
      return (
        <LargeHero
          eyebrow={eyebrow}
          eyebrowIcon={eyebrowIcon as ImageObject}
          headline={title}
          copy={body?.body}
          ctas={ctas}
          bgImage={backgroundImage as ImageObject}
          bgVideo={bgVideo}
          wistiaBgVideo={wistiaVideo!}
          darkOverlay={darkOverlay}
          reverseLayout={alignToRight}
          alignLayout={alignToRight ? "right" : alignLayout} // catch "alignToRight" props
          showLeadForm={showLeadForm}
          isHomePage={isHomePage}
          pardotFormHandlerUrl={pardotFormHandlerUrl}
          leadFormHeadline={formTitle}
          leadFormButtonText={formButtonText}
          leadFormCampaignId={leadFormCampaignId}
          leadFormConfirmationRoute={leadFormConfirmationRoute}
          leadFormLastInquiryObject={leadFormLastInquiryObject}
          titleWithLineBreaks={titleWithLineBreaks?.titleWithLineBreaks}
          useTitleWithLineBreaks={useTitleWithLineBreaks}
          form={formObj}
          onLeadFormSubmitError={onLeadFormSubmitError}
          quote={quoteObj}
          theme={theme}
        />
      );
    }
    default: {
      return (
        <PageHero
          eyebrow={eyebrow}
          eyebrowIcon={eyebrowIcon as ImageObject}
          headline={title}
          copy={body?.body}
          ctas={ctas}
          bgImage={backgroundImage as ImageObject}
          bgVideo={bgVideo}
          wistiaBgVideo={wistiaVideo!}
          darkOverlay={darkOverlay}
          reverseLayout={alignToRight}
          alignLayout={alignToRight ? "right" : alignLayout} // catch "alignToRight" props
          showLeadForm={showLeadForm}
          isHomePage={isHomePage}
          pardotFormHandlerUrl={pardotFormHandlerUrl}
          leadFormHeadline={formTitle}
          leadFormButtonText={formButtonText}
          leadFormCampaignId={leadFormCampaignId}
          leadFormConfirmationRoute={leadFormConfirmationRoute}
          leadFormLastInquiryObject={leadFormLastInquiryObject}
          titleWithLineBreaks={titleWithLineBreaks?.titleWithLineBreaks}
          useTitleWithLineBreaks={useTitleWithLineBreaks}
          form={formObj}
          onLeadFormSubmitError={onLeadFormSubmitError}
          quote={quoteObj}
          theme={theme}
        />
      );
    }
  }
};

export default HeroSideBySideModule;
